import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';

export interface MobileNavigationProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function MobileNavigation({ open, onClose, children }: MobileNavigationProps) {
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const first = drawerRef.current?.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();
    return () => previousFocusRef.current?.focus();
  }, [open]);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (!open) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...(drawerRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])];
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  return (
    <>
      {open && <button type="button" className="sidebar-overlay ihd-mobile-navigation__overlay" onClick={onClose} aria-label="Закрыть меню" />}
      <div
        ref={drawerRef}
        className={`ihd-mobile-navigation ${open ? 'ihd-mobile-navigation--open' : ''}`}
        role={open ? 'dialog' : undefined}
        aria-modal={open ? true : undefined}
        aria-label={open ? 'Мобильная навигация' : undefined}
        onKeyDown={handleKeyDown}
      >
        {children}
      </div>
    </>
  );
}
